import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select } from 'antd';
import { CalendarOutlined, BankOutlined, ApartmentOutlined } from '@ant-design/icons';
import { usePartnershipContext } from '../../../core/context/PartnershipContext';
import ClientService from '../../../features/ClientView/services/ClientService';
import EntityService from '../../../features/EntityView/services/EntityService';
import styles from './ClientBanner.module.css';

const bannerClientService = new ClientService();

const TAX_YEARS = ['FY2025', 'FY2024', 'FY2023', 'FY2022'];

interface ClientBannerProps {
  showPartnership?: boolean;
}

export function ClientBanner({ showPartnership = true }: ClientBannerProps) {
  const navigate = useNavigate();
  const {
    taxYear, setTaxYear,
    selectedClient, setSelectedClient,
    selectedPartnership, setSelectedPartnership,
    contextInitError,
  } = usePartnershipContext();

  const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
  const [partnerships, setPartnerships] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    bannerClientService.getAllClients()
      .then((data) => setClients(data.map((c) => ({ id: c.id, name: c.name }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Clear stale list immediately so old client's partnerships never show for a new client.
    setPartnerships([]);
    if (!selectedClient) return;

    // Cancellation flag prevents a slow response for a previous client from
    // overwriting the current client's results (race condition / Bug 2).
    let cancelled = false;
    EntityService.getEntitiesByClient(selectedClient.id)
      .then((data) => {
        if (cancelled) return;
        setPartnerships(
          data
            .filter((e) => e.entityType?.toLowerCase() === 'partnership')
            .map((e) => ({ id: String(e.id), name: e.name })),
        );
      })
      .catch(() => { if (!cancelled) setPartnerships([]); });

    return () => { cancelled = true; };
  // NOTE: do NOT call setSelectedPartnership(null) here — the onChange handler already
  // does it on an explicit client switch. Calling it here would wipe the stored
  // partnership selection when ClientBanner re-mounts after page navigation (Bug 3).
  }, [selectedClient]);

  return (
    <div className={styles.banner}>
      {contextInitError && (
        <div className={styles.pill} style={{ color: '#ef4444' }}>
          Failed to load — please refresh.
        </div>
      )}
      {/* Tax year */}
      <div className={styles.pill}>
        <CalendarOutlined className={styles.pillIcon} />
        <span className={styles.pillLabel}>TAX YEAR</span>
        <Select
          value={taxYear}
          onChange={setTaxYear}
          options={TAX_YEARS.map((y) => ({ value: y, label: y }))}
          size="small"
          variant="borderless"
          className={styles.pillSelect}
        />
      </div>

      {/* Client selector */}
      {clients.length > 0 && (
        <div className={styles.pill}>
          <BankOutlined className={styles.pillIcon} />
          <span
            className={styles.pillLabel}
            style={selectedClient ? { cursor: 'pointer', color: '#F26522' } : undefined}
            onClick={() => selectedClient && navigate('/overview')}
            title={selectedClient ? 'View client overview' : undefined}
          >
            CLIENT
          </span>
          <Select
            value={selectedClient?.id ?? undefined}
            onChange={(id) => {
              const client = clients.find((c) => c.id === id);
              if (client) {
                setSelectedClient({ id: client.id, name: client.name });
                setSelectedPartnership(null);
              }
            }}
            options={clients.map((c) => ({ value: c.id, label: c.name }))}
            size="small"
            variant="borderless"
            className={styles.pillSelect}
            placeholder="Select client..."
          />
        </div>
      )}

      {/* Partnership selector — only when showPartnership and client is selected */}
      {showPartnership && selectedClient && partnerships.length > 0 && (
        <div className={styles.pill}>
          <ApartmentOutlined className={styles.pillIcon} />
          <span className={styles.pillLabel}>PARTNERSHIP</span>
          <Select
            value={selectedPartnership?.id ?? undefined}
            onChange={(id) => {
              const p = partnerships.find((x) => x.id === id);
              if (p) {
                setSelectedPartnership({ id: p.id, name: p.name, entityType: 'Partnership' });
              }
            }}
            options={partnerships.map((p) => ({ value: p.id, label: p.name }))}
            size="small"
            variant="borderless"
            className={styles.pillSelect}
            placeholder="Select partnership..."
          />
        </div>
      )}
    </div>
  );
}
